// @flow
import * as React from 'react';

import {
  treeNodesMappedToRenderFunctions,
  stringAsDom,
  mightHaveTags,
} from './xml';
import type { RenderFunctionMap } from './xml';

export type Replacements = $ReadOnly<{ [string]: string | number }>;

export type Translations = $ReadOnly<{ [string]: string }>;

export type Translate = (
  str: string,
  replacements: Replacements,
  translations: Translations,
  locale: string,
) => string;

export type TranslateType = (
  key: string,
  replacements?: { [key: string]: string | number },
) => string;

type TranslateWithFallback = (
  str: string,
  replacements?: Replacements,
  translations: Translations,
  fallback: string,
) => string;

export const withReplacements = (
  str: string,
  replacements: Replacements,
): string => {
  if (!str) {
    throw new Error({ message: `Missing translation ${str}` });
  }

  // eslint-disable-next-line fp/no-let
  let result = str;

  Object.keys(replacements).forEach((replacement) => {
    if (!replacement) return;

    // eslint-disable-next-line fp/no-mutation
    result = result.replace(
      new RegExp(`%{${replacement}(=".*")?}`),
      String(replacements[replacement]),
    );
  });

  // if there are still any values that should be replaced, but a replacement
  // wasn't found for them, replace them with the default or an empty string
  // eslint-disable-next-line fp/no-mutation
  result = result.replace(/%{([^}]*)}/g, (_fullMatch, spec) => {
    const match = spec.match(/="(.*)"/);
    if (match?.[1]) return match[1];
    return '';
  });
  return result;
};

export const withTagReplacements = (
  str: string,
  replacements: RenderFunctionMap,
): React.Node =>
  mightHaveTags(str)
    ? treeNodesMappedToRenderFunctions(stringAsDom(str), replacements)
    : str;

export const translate: Translate = (
  str,
  replacements = { ...null },
  translations,
  locale,
) => {
  if (translations[str]) {
    return withReplacements(translations[str], replacements);
  }
  throw new Error(`Missing translation for ${str} in locale ${locale}.`);
};

export const translateWithFallback: TranslateWithFallback = (
  str,
  replacements = { ...null },
  translations,
  fallbackStr,
) => {
  if (translations[str]) {
    return withReplacements(translations[str], replacements);
  }
  return fallbackStr;
};

export type TranslateWithTagReplacements = (
  str: string,
  replacements: Replacements,
  tagReplacements: RenderFunctionMap,
  translations: Translations,
  locale: string,
) => React.Node;

export const translateWithTagReplacements: TranslateWithTagReplacements = (
  str,
  replacements = { ...null },
  tagReplacements = { ...null },
  translations,
  locale,
) => {
  if (translations[str]) {
    return withTagReplacements(
      translate(str, replacements, translations, locale),
      tagReplacements,
    );
  }
  throw new Error(`Missing translation for ${str} in locale ${locale}.`);
};

/**
 * @memberof modules/l10n
 */
export const pluralize = (
  count: number,
  singularForm: string,
  pluralForm: string,
): string => (count === 1 ? singularForm : pluralForm);
